import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/hooks/useAppStore';
import { GENERATION_ACTION_LOGO_URL } from '@/config/brand';
import ResultCard from '@/pages/generate/components/ResultCard';
import ModelSelector from '@/pages/generate/components/ModelSelector';
import QualitySelector from '@/pages/generate/components/QualitySelector';
import RatioModal from '@/pages/generate/components/RatioModal';
import { useAppConfig } from '@/hooks/useAppConfig';
import { type ToolGenerationControls, type ToolResult } from '../hooks/useToolGenerate';
import { useGenerationTasks } from '@/contexts/generationTaskStore';

interface ToolPageLayoutProps {
  title: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  description: string;
  guidance?: {
    input: string;
    output: string;
    fallback: string;
    beforeLabel?: string;
    afterLabel?: string;
    beforeHint?: string;
    afterHint?: string;
  };
  pointsCost: number | null;
  children: ReactNode;
  onGenerate: () => void;
  isGenerating: boolean;
  canGenerate: boolean;
  result: ToolResult | null;
  error: string | null;
  onRegenerate: () => void;
  onCancel: () => void;
  onClearError: () => void;
  controls: ToolGenerationControls;
}

export default function ToolPageLayout({
  title,
  icon,
  iconBg,
  description,
  guidance,
  pointsCost,
  children,
  onGenerate,
  isGenerating,
  canGenerate,
  result,
  error,
  onRegenerate,
  onCancel,
  onClearError,
  controls,
}: ToolPageLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAppStore();
  const config = useAppConfig();
  const { tasks } = useGenerationTasks();
  const [showModelSheet, setShowModelSheet] = useState(false);
  const [showQualitySheet, setShowQualitySheet] = useState(false);
  const [showRatioSheet, setShowRatioSheet] = useState(false);
  const entryTasks = tasks.filter((task) => task.tool_id === controls.toolId).slice(0, 3);
  const locationState = location.state as { returnTo?: unknown } | null;
  const from = new URLSearchParams(location.search).get('from');
  const returnTo = typeof locationState?.returnTo === 'string'
    ? locationState.returnTo
    : from === 'home'
      ? '/'
      : '/tools';
  const selectedModel = controls.models.find((model) => model.id === controls.modelId);
  const selectedRatio = config?.ratios?.find((ratio) => ratio.id === controls.ratio);
  const qualityLabel = t(`gen_quality_${controls.quality}_name`, {
    defaultValue: controls.quality,
  });
  const ratioLabel = controls.ratioMode === 'source'
    ? `${t('gen_followSource', '跟随原图')}${selectedRatio ? `（${selectedRatio.label}）` : ''}`
    : controls.ratioMode === 'fixed'
      ? `${t('gen_fixedRatio', '固定比例')} ${controls.fixedRatio ?? controls.ratio}`
      : selectedRatio?.label ?? controls.ratio ?? t('gen_ratioFallback');

  if (!user) {
    return (
      <div
        className="min-h-dvh font-nunito flex flex-col items-center justify-center px-5 relative overflow-hidden max-w-md mx-auto w-full"
        style={{
          background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)',
        }}
      >
        <div
          className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)',
            transform: 'translate(20%, -20%)',
          }}
        />
        <div
          className="absolute bottom-20 left-0 w-40 h-40 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)',
            transform: 'translate(-30%, 0)',
          }}
        />

        <div
          className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6"
          style={{
            background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)',
          }}
        >
          <i className="ri-image-ai-line text-white text-4xl" />
        </div>

        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('gen_loginTitle')}</h2>
        <p className="text-sm text-gray-400 mb-8 text-center leading-relaxed whitespace-pre-line">
          {t('gen_loginDesc')}
        </p>
        <button
          onClick={() => navigate(`/login?redirect=${window.location.pathname}`)}
          className="w-full max-w-xs py-4 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap"
          style={{
            background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)',
          }}
        >
          {t('gen_loginBtn')}
        </button>
      </div>
    );
  }

  return (
    <div
      className="min-h-dvh font-nunito flex flex-col relative overflow-hidden max-w-md mx-auto w-full"
      style={{
        background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)',
      }}
    >
      {/* 背景 */}
      <div
        className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)',
          transform: 'translate(20%, -20%)',
        }}
      />
      <div
        className="absolute top-32 left-0 w-40 h-40 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)',
          transform: 'translate(-30%, 0)',
        }}
      />

      {/* Header */}
      <div className="relative z-10 flex items-center gap-3 px-4 pt-safe pb-3">
        <button
          onClick={() => navigate(returnTo)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-95 transition-transform"
          style={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <i className="ri-arrow-left-line text-gray-600 text-base" />
        </button>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: iconBg }}
        >
          <i className={`${icon} text-white text-base`} />
        </div>
        <h1 className="text-base font-bold text-gray-800">{title}</h1>
        <div className="ml-auto flex items-center gap-1">
          <i className="ri-flashlight-fill text-amber-500 text-xs" />
          <span className="text-xs font-bold text-gray-700">{user.points}</span>
        </div>
      </div>

      {/* Content */}
      <div
        className="relative z-10 flex-1 overflow-y-auto ios-scroll px-4"
        style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
      >
        {/* 描述 */}
        <p className="text-xs text-gray-400 mb-4 leading-relaxed">{description}</p>

        {guidance && (
          <div
            className="rounded-3xl p-4 mb-4"
            style={{
              background: 'rgba(255,255,255,0.86)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,107,157,0.12)',
            }}
          >
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-2xl overflow-hidden bg-gray-100">
                <div className="aspect-[4/3] flex items-center justify-center px-3 text-center" style={{ background: 'linear-gradient(135deg, #F3F4F6, #E5E7EB)' }}>
                  <div>
                    <i className="ri-image-line text-2xl text-gray-400" />
                    <p className="text-[11px] font-semibold text-gray-500 mt-1">{guidance.beforeHint}</p>
                  </div>
                </div>
                <p className="px-3 py-2 text-[11px] font-bold text-gray-500">{guidance.beforeLabel ?? t('tool_before')}</p>
              </div>
              <div className="rounded-2xl overflow-hidden bg-gray-100">
                <div className="aspect-[4/3] flex items-center justify-center px-3 text-center" style={{ background: iconBg }}>
                  <div>
                    <i className="ri-sparkling-2-line text-2xl text-white" />
                    <p className="text-[11px] font-semibold text-white/90 mt-1">{guidance.afterHint}</p>
                  </div>
                </div>
                <p className="px-3 py-2 text-[11px] font-bold text-gray-500">{guidance.afterLabel ?? t('tool_after')}</p>
              </div>
            </div>
            <div className="space-y-2">
              {[
                ['ri-upload-cloud-2-line', t('tool_inputRequirement'), guidance.input],
                ['ri-magic-line', t('tool_expectedResult'), guidance.output],
                ['ri-lifebuoy-line', t('tool_ifFails'), guidance.fallback],
              ].map(([itemIcon, label, text]) => (
                <div key={label} className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,107,157,0.10)' }}>
                    <i className={`${itemIcon} text-sm text-pink-500`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold text-gray-700">{label}</p>
                    <p className="text-[11px] text-gray-400 leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 上传和参数区域 */}
        {children}

        <div className="flex items-center justify-end gap-1.5 mt-3" data-testid="tool-output-controls">
          <button
            type="button"
            data-testid="tool-model-control"
            onClick={() => setShowModelSheet(true)}
            disabled={controls.models.length === 0 || isGenerating}
            className="flex min-w-0 items-center gap-1 rounded-full px-2.5 py-1 active:scale-95 disabled:opacity-50"
            style={{ background: 'rgba(255,107,157,0.08)', border: '1px solid rgba(255,107,157,0.12)' }}
          >
            <i className={`${selectedModel?.icon ?? 'ri-robot-2-line'} text-[11px] text-gray-500`} />
            <span className="max-w-28 truncate text-[11px] font-medium text-gray-600">
              {selectedModel?.name_zh ?? selectedModel?.name ?? t('gen_noAvailableModels', '无可用模型')}
            </span>
            <i className="ri-arrow-down-s-line text-[10px] text-gray-400" />
          </button>

          <button
            type="button"
            data-testid="tool-ratio-control"
            onClick={() => controls.ratioMode === 'selectable' && setShowRatioSheet(true)}
            disabled={controls.ratioMode !== 'selectable' || isGenerating}
            className="flex min-w-0 items-center gap-1 rounded-full px-2.5 py-1 active:scale-95 disabled:opacity-70"
            style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}
          >
            <i className="ri-aspect-ratio-line text-[11px] text-gray-500" />
            <span className="max-w-28 truncate text-[11px] font-medium text-gray-600">{ratioLabel}</span>
            {controls.ratioMode === 'selectable' && <i className="ri-arrow-down-s-line text-[10px] text-gray-400" />}
          </button>

          <button
            type="button"
            data-testid="tool-quality-control"
            onClick={() => setShowQualitySheet(true)}
            disabled={controls.supportedQualities.length === 0 || isGenerating}
            className="flex min-w-0 items-center gap-1 rounded-full px-2.5 py-1 active:scale-95 disabled:opacity-50"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}
          >
            <i className="ri-image-2-line text-[11px] text-gray-500" />
            <span className="max-w-24 truncate text-[11px] font-medium text-gray-600">{qualityLabel}</span>
            <i className="ri-arrow-down-s-line text-[10px] text-gray-400" />
          </button>
        </div>

        {entryTasks.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="tool-task-strip">
            {entryTasks.map((task) => (
              <button
                key={task.gen_id}
                type="button"
                onClick={() => navigate('/profile?tab=history')}
                className="flex w-full items-center gap-2 rounded-2xl bg-white/80 px-3 py-2 text-left text-xs"
              >
                <i className={['success', 'failed', 'blocked', 'timeout'].includes(task.status)
                  ? (task.status === 'success'
                    ? 'ri-checkbox-circle-fill text-emerald-500'
                    : 'ri-error-warning-fill text-red-400')
                  : 'ri-loader-4-line animate-spin text-purple-500'} />
                <span className="min-w-0 flex-1 truncate text-gray-600">
                  {task.status === 'success'
                    ? t('gen_taskComplete', '生成完成')
                    : ['failed', 'blocked', 'timeout'].includes(task.status)
                      ? t('gen_taskFailed', '生成未完成')
                      : t('gen_generationContinuesInBackground', '后台生成中')}
                </span>
                <span className="text-gray-400">{task.model_id} · {task.quality}</span>
              </button>
            ))}
          </div>
        )}

        {/* 生成按钮 */}
        {!isGenerating && !result && (
          <button
            onClick={() => {
              onClearError();
              onGenerate();
            }}
            disabled={!canGenerate || pointsCost === null || controls.capacityFull}
            data-testid="tool-generate-btn"
            className="w-full py-4 rounded-2xl text-white font-bold text-base cursor-pointer active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            style={{
              background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)',
            }}
          >
            {t('gen_generate')}
          </button>
        )}

        {controls.capacityFull && !isGenerating && !result && (
          <p className="mt-2 text-center text-xs text-amber-500">
            {t('gen_concurrencyLimitReached', '并发任务已满，请等待已有任务完成')}
          </p>
        )}

        {/* 积分信息 */}
        {!isGenerating && !result && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            <p className="text-xs text-gray-400">
              {t('gen_pointsLeft')}{' '}
              <span className="font-semibold text-gray-600">{user.points}</span>{' '}
              {t('gen_points')} · {t('gen_pointsCost', { cost: pointsCost ?? '--' })}
            </p>
          </div>
        )}

        {/* 错误态 */}
        {error && !isGenerating && !result && (
          <div
            data-testid="tool-error"
            className="mt-4 rounded-2xl bg-red-50 border border-red-100 px-4 py-3"
          >
            <div className="flex items-start gap-2.5">
              <i className="ri-error-warning-line text-red-500 text-lg mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-red-600">{t('gen_genFailed')}</p>
                <p className="text-xs text-red-500/80 mt-1 leading-relaxed break-words">{error}</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={onClearError}
                className="py-2.5 rounded-xl bg-white text-red-500 text-sm font-semibold border border-red-100"
              >
                {t('gen_cancel')}
              </button>
              <button
                type="button"
                onClick={onRegenerate}
                className="py-2.5 rounded-xl text-white text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}
              >
                {t('gen_retry')}
              </button>
            </div>
          </div>
        )}

        {/* 生成中 */}
        {isGenerating && (
          <div className="mt-8 flex flex-col items-center gap-5">
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-full opacity-20 animate-ping"
                style={{
                  background: 'linear-gradient(135deg, #FF6B9D, #A855F7)',
                  animationDuration: '1.5s',
                }}
              />
              <div
                className="absolute inset-2 rounded-full opacity-30"
                style={{
                  background: 'linear-gradient(135deg, #FF6B9D, #A855F7)',
                }}
              />
              <div
                className="relative w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden bg-white"
                style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
              >
                <img
                  src={GENERATION_ACTION_LOGO_URL}
                  alt="logo"
                  className="w-12 h-12 object-contain rounded-xl animate-pulse"
                />
              </div>
              <div
                className="absolute inset-0 rounded-full border-2 border-dashed border-pink-300/60 animate-spin"
                style={{ animationDuration: '6s' }}
              />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_aiCreating')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('gen_aiCreatingDesc')}</p>
            </div>
            <div
              className="w-52 h-1.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,107,157,0.15)' }}
            >
              <div
                className="h-full rounded-full animate-progress"
                style={{
                  background: 'linear-gradient(90deg, #FF6B9D, #A855F7)',
                }}
              />
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 rounded-full bg-white/80 text-gray-600 text-sm font-semibold shadow-sm"
            >
              {t('gen_cancel')}
            </button>
          </div>
        )}

        {/* 结果 */}
        {result && !isGenerating && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <div
                className="w-1.5 h-6 rounded-full"
                style={{
                  background: 'linear-gradient(180deg, #FF6B9D, #A855F7)',
                }}
              />
              <h2 className="text-base font-bold text-gray-800">{t('gen_result')}</h2>
              <div
                className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white"
                style={{
                  background: 'linear-gradient(135deg, #FF6B9D, #A855F7)',
                }}
              >
                <i className="ri-check-line text-xs" />
                <span>{t('gen_success')}</span>
              </div>
            </div>
            <ResultCard
              imageUrl={result.imageUrl}
              thumbnailUrl={result.thumbnailUrl}
              styleName={result.styleName}
              ratio={result.ratio}
              prompt={result.prompt}
              onRegenerate={onRegenerate}
              isVip={user?.isVip || false}
              genId={result.gen_id ?? ''}
              canDownloadHd={false}
            />
          </div>
        )}
      </div>

      <ModelSelector
        value={controls.modelId}
        onChange={controls.setModelId}
        disabled={isGenerating}
        isOpen={showModelSheet}
        onClose={() => setShowModelSheet(false)}
        quality={controls.quality}
        mode={controls.toolId}
        referenceImageCount={1}
      />

      {showQualitySheet && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowQualitySheet(false)} />
          <div className="relative w-full rounded-t-3xl bg-white px-5 pt-5" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-800">{t('gen_qualityFallback')}</h3>
              <button type="button" onClick={() => setShowQualitySheet(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <QualitySelector
              value={controls.quality}
              onChange={(value) => { controls.setQuality(value); setShowQualitySheet(false); }}
              isVip={user.isVip || false}
              disabled={isGenerating}
              modelId={controls.modelId}
              supportedQualities={controls.supportedQualities}
            />
          </div>
        </div>
      )}

      <RatioModal
        isOpen={showRatioSheet && controls.ratioMode === 'selectable'}
        selectedId={controls.ratio || null}
        onClose={() => setShowRatioSheet(false)}
        onSelect={controls.setRatio}
        allowedRatioIds={controls.supportedRatios}
      />
    </div>
  );
}
